import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Hash, Users, Store, MessageCircle, Bell, BellOff, Brain, Zap, Globe,
  TrendingUp, Crown, Play, MapPin,
} from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { getSocket } from '../lib/socket';
import PostCard from '../components/PostCard';
import Composer from '../components/Composer';
import TriviaChatCard from '../components/TriviaChatCard';
import {
  SkeletonList, Empty, ShopCard, Avatar, RankBadge, SectionHead,
} from '../components/ui';
import { flagOf } from '../lib/countries';

const TABS = [
  ['feed', 'Feed', MessageCircle],
  ['trivia', 'Trivia', Brain],
  ['shops', 'Shops', Store],
  ['members', 'People', Users],
  ['top', 'Ranking', TrendingUp],
];

export default function GroupPage() {
  const { handle } = useParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [tab, setTab] = useState('feed');
  const [liveRunning, setLiveRunning] = useState(false);
  const h = decodeURIComponent(handle);
  const isAdmin = user?.role === 'admin';

  /* ---------------- QUERIES ---------------- */
  const { data: g, isLoading } = useQuery({
    queryKey: ['group', h],
    queryFn: () => api.get(`/groups/${encodeURIComponent(h)}`).then(r => r.data),
  });

  const { data: posts } = useQuery({
    queryKey: ['feed', 'group', h],
    queryFn: () => api.get('/posts', { params: { group: h, sort: 'hot', limit: 30 } })
      .then(r => r.data),
    enabled: tab === 'feed',
  });

  const { data: shops } = useQuery({
    queryKey: ['groupShops', g?.id],
    queryFn: () => api.get(`/groups/${g.id}/shops`).then(r => r.data),
    enabled: !!g && tab === 'shops',
  });

  const { data: members } = useQuery({
    queryKey: ['groupMembers', g?.id],
    queryFn: () => api.get(`/groups/${g.id}/members`).then(r => r.data),
    enabled: !!g && tab === 'members',
  });

  const { data: lb } = useQuery({
    queryKey: ['groupLb', h],
    queryFn: () => api.get('/leaderboard', { params: { scope: `city:${h}`, limit: 30 } })
      .then(r => r.data),
    enabled: tab === 'top',
  });

  const { data: trivia } = useQuery({
    queryKey: ['triviaStatus', g?.id],
    queryFn: () => api.get('/trivia/status', { params: { group_id: g.id } }).then(r => r.data),
    enabled: !!g,
    refetchInterval: tab === 'trivia' ? 15000 : false,
  });

  const { data: triviaLb } = useQuery({
    queryKey: ['triviaLb', g?.id],
    queryFn: () => api.get('/trivia/leaderboard', { params: { group_id: g.id } })
      .then(r => r.data),
    enabled: !!g && tab === 'trivia',
  });

  /* ---------------- SOCKET ---------------- */
  useEffect(() => {
    if (!g?.id) return;
    const s = getSocket(); if (!s) return;

    s.emit('join', `group:${g.id}`);
    s.emit('trivia:live:state', { groupId: g.id }, (st) => setLiveRunning(!!st?.running));

    const onStart = () => {
      setLiveRunning(true);
      toast('🧠 Live Trivia started!', { icon: '⚡' });
    };
    const onEnd = () => { setLiveRunning(false); qc.invalidateQueries(['triviaLb', g.id]); };
    const onPost = (p) => { if (p.group_id === g.id) qc.invalidateQueries(['feed', 'group', h]); };

    s.on('trivia:live:started', onStart);
    s.on('trivia:live:finished', onEnd);
    s.on('trivia:live:cancelled', onEnd);
    s.on('post:new', onPost);

    return () => {
      s.off('trivia:live:started', onStart);
      s.off('trivia:live:finished', onEnd);
      s.off('trivia:live:cancelled', onEnd);
      s.off('post:new', onPost);
    };
  }, [g?.id, h, qc]);

  /* ---------------- MUTATIONS ---------------- */
  const joinM = useMutation({
    mutationFn: () => api.post(`/groups/${g.id}/${g.joined ? 'leave' : 'join'}`),
    onSuccess: async () => {
      toast.success(g.joined ? 'Left the group' : `Joined ${g.handle}! 🎉`);
      qc.invalidateQueries(['group', h]);
      await useAuth.getState().load();
    },
    onError: e => toast.error(e.message),
  });

  const setHomeM = useMutation({
    mutationFn: () => api.post('/groups/set-home', { cityGroupId: g.id }),
    onSuccess: async () => {
      toast.success(`${g.handle} is now your home city 🏠`);
      await useAuth.getState().load();
      qc.invalidateQueries(['group', h]);
    },
    onError: e => toast.error(e.message),
  });

  function startLiveTrivia() {
    if (!g.joined) return toast.error('Join the group first');
    getSocket()?.emit('trivia:live:start', { groupId: g.id }, (r) => {
      if (r?.error) toast.error(r.error);
      else toast.success('Live trivia starting! 🧠');
    });
  }

  if (isLoading || !g) return <SkeletonList n={3} h="h-40" />;

  const isCountry = g.kind === 'country';
  const isWorld = g.kind === 'world';
  const isHome = user?.city_group_id === g.id;
  const population = g.population || g.stats?.population || 0;

  return (
    <div className="space-y-4 animate-fade-up">
      {/* ═══════ HEADER ═══════ */}
      <div className="card overflow-hidden">
        <div className={`h-24 relative ${
          isWorld ? 'bg-gradient-to-r from-indigo-500 to-cyan-600'
            : isCountry ? 'bg-gradient-to-r from-grape-500 to-indigo-700'
            : 'bg-gradient-to-r from-brand-400 via-brand-500 to-grape-600'}`}>
          <motion.div className="absolute inset-0 opacity-30"
            style={{ background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,.5), transparent 50%)' }}
            animate={{ x: [0, 40, 0] }} transition={{ duration: 8, repeat: Infinity }} />

          {liveRunning && (
            <motion.div className="absolute top-3 right-3 flex items-center gap-1.5 bg-red-500
              text-white px-2.5 py-1 rounded-full text-[10px] font-black shadow-lg"
              animate={{ scale: [1, 1.06, 1] }} transition={{ duration: 1.2, repeat: Infinity }}>
              <span className="w-1.5 h-1.5 rounded-full bg-white" /> LIVE TRIVIA
            </motion.div>
          )}
        </div>

        <div className="p-4 -mt-10">
          <div className="flex items-end gap-3">
            <div className="w-20 h-20 rounded-2xl bg-white shadow-lg grid place-items-center
              ring-4 ring-white shrink-0">
              {isWorld ? <Globe size={30} className="text-indigo-600" />
                : isCountry ? <span className="text-3xl">{flagOf(g.country_code)}</span>
                : g.is_capital ? <span className="text-3xl">⭐</span>
                : <Hash size={32} className="text-brand-500" />}
            </div>

            <div className="flex-1 pb-1 min-w-0">
              <h1 className="font-display text-2xl font-extrabold truncate">{g.handle}</h1>
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="text-sm text-ink-500 font-semibold truncate">{g.name}</p>
                {g.country_code && !isCountry && (
                  <span className="text-sm">{flagOf(g.country_code)}</span>
                )}
                {isHome && <span className="chip-brand !text-[8px]">HOME</span>}
              </div>
            </div>

            <button onClick={() => joinM.mutate()} disabled={joinM.isPending}
              className={`${g.joined ? 'btn-ghost' : 'btn-primary'} btn-sm shrink-0`}>
              {g.joined ? <><BellOff size={13} /> Leave</> : <><Bell size={13} /> Join</>}
            </button>
          </div>

          {/* ─── STATS ─── */}
          <div className="grid grid-cols-3 gap-2 mt-4">
            <Stat icon={Users}
              value={population > 0 ? compact(population) : '—'}
              label={isAdmin ? `Pop · 👥${compact(g.stats?.members || 0)}` : 'Population'} />
            <Stat icon={Store} value={g.stats?.shops || 0} label="Shops" />
            <Stat icon={MessageCircle} value={compact(g.stats?.posts || 0)} label="Posts" />
          </div>

          {g.region && (
            <p className="text-[11px] text-ink-400 font-semibold mt-2 text-center">
              <MapPin size={9} className="inline" /> {g.region}
              {g.country && ` · ${g.country.name}`}
              {g.is_capital && ' · Capital city'}
            </p>
          )}

          {/* ─── ACTIONS ─── */}
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Link to={`/chat/group/${g.id}`} className="btn-dark btn-sm">
              <MessageCircle size={14} /> Live chat
            </Link>
            {trivia?.enabled ? (
              <button onClick={startLiveTrivia} disabled={liveRunning || !g.joined}
                className={`btn-sm ${liveRunning ? 'btn-ghost' : 'btn-grape'}`}>
                <Brain size={14} /> {liveRunning ? 'Running…' : 'Start trivia'}
              </button>
            ) : (
              <span className="btn-ghost btn-sm opacity-50 pointer-events-none">
                <Brain size={14} /> Trivia off
              </span>
            )}
          </div>

          {g.kind === 'city' && !isHome && g.joined && (
            <button onClick={() => setHomeM.mutate()} disabled={setHomeM.isPending}
              className="btn-ghost btn-sm w-full mt-2">
              <MapPin size={13} /> Set as my home city
            </button>
          )}
        </div>
      </div>

      {/* ═══════ TABS ═══════ */}
      <div className="flex gap-1 bg-white p-1 rounded-xl border border-ink-100
        overflow-x-auto no-scrollbar sticky top-14 z-20">
        {TABS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`relative flex-1 min-w-[74px] flex items-center justify-center gap-1 py-2
              rounded-lg text-xs font-bold transition
              ${tab === k ? 'bg-ink-900 text-white' : 'text-ink-500'}`}>
            <Icon size={12} /> {l}
            {k === 'trivia' && liveRunning && (
              <span className="absolute top-1 right-2 w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            )}
          </button>
        ))}
      </div>

      {/* ═══════ FEED ═══════ */}
      {tab === 'feed' && (
        <div className="space-y-3">
          {liveRunning && (
            <Link to={`/chat/group/${g.id}`}
              className="card p-3 flex items-center gap-3 bg-gradient-to-r from-red-500 to-brand-600 text-white">
              <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1.1, repeat: Infinity }}>
                <Brain size={20} />
              </motion.div>
              <div className="flex-1">
                <p className="font-bold text-sm">Live Trivia is running now!</p>
                <p className="text-[11px] opacity-85">Jump into the chat and score points 🧠</p>
              </div>
              <Play size={16} />
            </Link>
          )}

          {g.joined && <Composer groupHandle={g.handle} />}

          {!posts ? <SkeletonList n={3} h="h-48" />
            : !posts.length
              ? <Empty icon={MessageCircle} title="No posts yet"
                  sub={g.joined ? 'Be the first to post here.' : 'Join the group to post.'} />
              : posts.map(p => <PostCard key={p.id} post={p} />)}
        </div>
      )}

      {/* ═══════ TRIVIA ═══════ */}
      {tab === 'trivia' && (
        <div className="space-y-4">
          {!trivia?.enabled ? (
            <Empty icon={Brain} title="Trivia is disabled here"
              sub={trivia?.reason || 'An admin can enable trivia for this city.'} />
          ) : (
            <>
              <div className="card p-4 bg-gradient-to-br from-indigo-600 to-grape-700 text-white">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/20 grid place-items-center shrink-0">
                    <Brain size={22} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg font-extrabold">{g.handle} Trivia</p>
                    <p className="text-white/85 text-xs">
                      <Globe size={10} className="inline" /> {trivia.config.languageName} ·
                      {' '}{trivia.config.difficulty} · {trivia.config.questions} questions
                    </p>
                  </div>
                </div>

                {trivia.sponsor && (
                  <Link to={`/shop/${trivia.sponsor.slug}`}
                    className="flex items-center gap-2 mt-3 bg-white/15 rounded-xl p-2.5">
                    <Avatar src={trivia.sponsor.logo_url} name={trivia.sponsor.name} size={28} square />
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] font-black opacity-70 uppercase">Sponsored by</p>
                      <p className="font-bold text-sm truncate">{trivia.sponsor.name}</p>
                    </div>
                    {trivia.config.sponsor_multiplier > 1 && (
                      <span className="chip bg-amber-400 text-ink-900">
                        ×{trivia.config.sponsor_multiplier}
                      </span>
                    )}
                  </Link>
                )}

                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Link to="/trivia" className="btn bg-white text-grape-700 btn-sm font-extrabold">
                    <Zap size={13} /> Solo round
                  </Link>
                  <button onClick={startLiveTrivia} disabled={liveRunning || !g.joined}
                    className="btn bg-white/20 text-white btn-sm font-extrabold">
                    <Brain size={13} /> {liveRunning ? 'Running…' : 'Live in chat'}
                  </button>
                </div>
              </div>

              {liveRunning && (
                <div>
                  <SectionHead title="Live now" sub="Answer here or in the chat" />
                  <TriviaChatCard groupId={g.id} />
                </div>
              )}

              {trivia.live?.enabled && (
                <div className="card p-4">
                  <SectionHead title="Live trivia rules" sub="Everyone in the chat plays together" />
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <Mini v={trivia.live.questions} l="Questions" />
                    <Mini v={`${trivia.live.seconds}s`} l="Per question" />
                    <Mini v={`+${trivia.live.pointsPerCorrect}`} l="Per correct" />
                    <Mini v={`+${trivia.live.firstBonus}`} l="First 🥇" />
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(trivia.live.podiumBonus || []).map((p, i) => (
                      <span key={i} className="chip-brand">{['🥇', '🥈', '🥉'][i]} +{p}💎</span>
                    ))}
                    <span className="chip-grape">🎯 +{trivia.config.perfect_bonus} perfect</span>
                    <span className="chip-ink">⭐ +{trivia.config.xp_per_correct} XP each</span>
                  </div>
                  <p className="text-[11px] text-ink-400 font-semibold mt-2">
                    💡 Type <b className="text-grape-600">!trivia</b> in {g.handle} chat to launch
                    a round (min. {trivia.live.minPlayers} players online).
                  </p>
                </div>
              )}

              <div className="card p-4">
                <SectionHead title="Solo reward table" sub="Points per correct answers" />
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
                  {trivia.config.reward_tiers.map(t => (
                    <div key={t.correct} className="bg-ink-50 rounded-xl p-2 text-center">
                      <p className="font-black text-sm">
                        {t.correct}<span className="text-[10px] text-ink-400">✓</span>
                      </p>
                      <p className="text-[11px] font-black text-brand-600">+{t.points}💎</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <span className="chip-brand">🔥 +{trivia.config.streak_bonus}/streak</span>
                  <span className="chip-mint">⚡ up to +{trivia.config.speed_bonus_max} speed</span>
                </div>
              </div>

              <div>
                <SectionHead title="Trivia champions" sub={g.handle}
                  right={trivia.myStats && (
                    <span className="chip-grape">You: {trivia.myStats.total_correct} ✓</span>
                  )} />
                {!triviaLb ? <SkeletonList n={5} h="h-14" />
                  : !triviaLb.rows?.length
                    ? <Empty icon={Brain} title="No trivia scores yet"
                        sub="Be the first champion of this city!" />
                    : <div className="card p-2 space-y-1">
                        {triviaLb.rows.slice(0, 20).map(u => (
                          <Link key={u.user_id} to={`/u/${u.username}`}
                            className={`flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50
                              ${u.user_id === user.id ? 'bg-brand-50 ring-1 ring-brand-200' : ''}`}>
                            <RankBadge rank={u.rank} />
                            <Avatar src={u.image_url || u.avatar_url} name={u.username} size={36} square />
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-sm truncate">{u.hero_name || u.username}</p>
                              <p className="text-[11px] text-ink-400 font-semibold">
                                {u.accuracy}% acc · 🔥{u.best_streak} · {u.perfect_rounds} perfect
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-black text-sm text-grape-600">{u.total_correct}</p>
                              <p className="text-[9px] font-bold text-ink-400">CORRECT</p>
                            </div>
                          </Link>
                        ))}
                      </div>}
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══════ SHOPS ═══════ */}
      {tab === 'shops' && (
        <div className="space-y-2">
          {!shops ? <SkeletonList n={4} h="h-20" />
            : !shops.length
              ? <Empty icon={Store} title="No shops yet"
                  sub="Are you a local producer? Open your shop and reward this community."
                  action={<Link to="/create-shop" className="btn-primary btn-sm">Open a shop</Link>} />
              : shops.map(s => <ShopCard key={s.id} shop={s} />)}
        </div>
      )}

      {/* ═══════ MEMBERS ═══════ */}
      {tab === 'members' && (
        <div className="card p-2 space-y-1">
          {!members ? <SkeletonList n={6} h="h-14" />
            : !members.length
              ? <Empty icon={Users} title="No one here yet" sub="Be the first to join!" />
              : members.map(m => (
                  <Link key={m.id} to={`/u/${m.username}`}
                    className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50">
                    <Avatar src={m.image_url || m.avatar_url} name={m.username} size={40} square />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="font-bold text-sm truncate">
                          {m.hero_name || m.display_name || m.username}
                        </p>
                        {m.role === 'producer' && <span className="chip-mint !text-[8px]">SHOP</span>}
                      </div>
                      <p className="text-[11px] text-ink-400 font-semibold">
                        @{m.username}{m.level ? ` · L${m.level} · ELO ${m.elo}` : ''}
                        {m.gear_score > 0 && ` · ${m.gear_score}GS`}
                      </p>
                    </div>
                    <span className="chip-brand">💎 {compact(m.points_total)}</span>
                  </Link>
                ))}
        </div>
      )}

      {/* ═══════ RANKING ═══════ */}
      {tab === 'top' && (
        <div className="card p-2 space-y-1">
          {!lb ? <SkeletonList n={6} h="h-14" />
            : !lb.rows?.length ? <Empty icon={Crown} title="No ranking yet" />
              : lb.rows.map(u => (
                  <Link key={u.id} to={`/u/${u.username}`}
                    className={`flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50
                      ${u.id === user.id ? 'bg-brand-50 ring-1 ring-brand-200' : ''}`}>
                    <RankBadge rank={u.rank} />
                    <Avatar src={u.image_url || u.avatar_url} name={u.username} size={38} square />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="font-bold text-sm truncate">{u.hero_name || u.username}</p>
                        {u.guild_tag && <span className="chip-grape !text-[8px]">{u.guild_tag}</span>}
                      </div>
                      <p className="text-[11px] text-ink-400 font-semibold">
                        L{u.level} · {u.wins}W/{u.losses}L{u.streak > 0 ? ` · 🔥${u.streak}` : ''}
                      </p>
                    </div>
                    <span className="font-black text-sm text-brand-600">{compact(u.score)}</span>
                  </Link>
                ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- helpers ---------------- */
function Stat({ icon: Icon, value, label }) {
  return (
    <div className="bg-ink-50 rounded-xl p-2.5 text-center">
      <Icon size={15} className="mx-auto text-ink-400 mb-1" />
      <p className="font-black text-base">{value}</p>
      <p className="text-[9px] font-bold text-ink-400 uppercase truncate">{label}</p>
    </div>
  );
}

function Mini({ v, l }) {
  return (
    <div className="bg-ink-50 rounded-xl p-2 text-center">
      <p className="font-black text-sm">{v}</p>
      <p className="text-[9px] font-bold text-ink-400 uppercase">{l}</p>
    </div>
  );
}